/**
 * Platform-aware payment routing.
 *
 * Apple App Store Guideline 3.1.1:
 * - All digital features/subscriptions sold within an iOS app MUST use Apple IAP.
 * - Real-world services (event tickets, physical goods) are exempt and may use Stripe.
 *
 * Web and Android always use Stripe for all purchases.
 */

import { Capacitor } from "@capacitor/core";

/**
 * Returns true when running inside the native iOS Capacitor app.
 * In this context, subscription purchases MUST go through Apple IAP —
 * redirecting to Stripe checkout for digital content violates App Store guidelines.
 */
export function shouldUseAppleIAP(): boolean {
  if (typeof window === "undefined") return false;
  const isNativeIOS = Capacitor.getPlatform() === "ios";
  return isNativeIOS;
}

/**
 * Apple StoreKit product IDs — these MUST match App Store Connect exactly.
 *
 * SETUP REQUIRED before releasing to App Store:
 * 1. Open App Store Connect → Your App → In-App Purchases
 * 2. Create "Auto-Renewable Subscription" products with the IDs below
 * 3. Configure subscription groups, pricing, and localisation
 * 4. Ensure products are in "Ready to Submit" or "Approved" state
 *
 * @see https://developer.apple.com/documentation/storekit/in-app_purchase
 */
export { APPLE_PRODUCT_IDS, getAppleProductId } from "./iap-products";
